import {
  Chip,
  InfoCard,
  SidebarDelimiter,
  TagWithIcon,
  formatDate,
  Button,
  useDialog,
} from "@endya-conformite/ui-kit";
import ArrowRight from "@endya-conformite/ui-kit/src/assets/icons/arrow-right.svg?react";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import ChevronRight from "@endya-conformite/ui-kit/src/assets/icons/chevron-right.svg?react";
import Warning from "@endya-conformite/ui-kit/src/assets/icons/warning.svg?react";
import { ControleQuinquennalAxeStatus } from "@gateway/controle-quinquennal/controller/controle-quinquennal.definition";
import { useAvancementSocieteQuery } from "@src/data/avancement/hook/useAvancementSocieteQuery";
import { useCancelCQBatchMutation } from "@src/data/controle-quinquennal/hook/useCancelCQBatchMutation";
import { useCQWithAxesQuery } from "@src/data/controle-quinquennal/hook/useCQWithAxesQuery";
import { useInfoSocieteOriasQuery } from "@src/data/infoSocieteOrias/hook/useInfoSocieteOriasQuery";
import {
  AXE_STATUS_MAP,
  getControleQuinquennalDateButoir,
  isControleQuinquennalReadOnly,
} from "@src/helper/ticket.helper";
import { CQCancelDialog } from "@src/pages/controle-quinquennal/components/CQCancelDialog";
import classNames from "classnames";
import { ReactNode, useMemo } from "react";
import { ChipColor } from "@endya-conformite/ui-kit/src/components/Chip/Chip";
import { StatutSociete } from "@gateway/info-societe-orias/controller/info-societe-orias.definition";
import { axeTypeToLabel } from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import { isRecette } from "@src/helper/environnement.helper";
import { EntrepriseActionsMenu } from "./EntrepriseActionsMenu";
import { Conversation } from "./Conversation";
import { SidebarInformationsEntrepriseProps } from "../sidebar.types";
import style from "./SidebarInformationsEntreprise.module.scss";

type ZoneInfoProps = {
  title: string;
  value?: string;
  status?: ControleQuinquennalAxeStatus;
  warning?: ReactNode;
};

function ZoneInfo({ title, value, status, warning }: ZoneInfoProps) {
  const config = status ? AXE_STATUS_MAP[status] : undefined;

  return (
    <div className={style.zoneInfo}>
      <div>
        <p>{title}</p>
        <span className={style.warning}>{warning}</span>
        <h4>{value}</h4>
      </div>
      {config !== undefined && (
        <TagWithIcon variant={config.variant} overrideIcon={config.icon}>
          {config.label}
        </TagWithIcon>
      )}
    </div>
  );
}

const formatList = (items?: string[]) =>
  items?.length ? items.join(", ") : "-";

export function SidebarInformationsEntreprise({
  axeData,
  setSidePaneState,
  entreprise,
  conversations,
  millesime,
  collapsed,
  onToggle,
}: SidebarInformationsEntrepriseProps) {
  const latestConversation = conversations?.[0];
  const latestRemediation = latestConversation?.remediation;

  const { data: infoSocieteOrias, isLoading: isOriasLoading } =
    useInfoSocieteOriasQuery(entreprise.siren);

  const { data: avancementSociete } = useAvancementSocieteQuery(
    entreprise.siren,
    millesime
  );

  const { data: controleQuinquennal } = useCQWithAxesQuery(
    entreprise.hubspotId
  );
  const { openDialog } = useDialog();
  const annulationControle = useCancelCQBatchMutation();

  const handleAnnulerControle = () => {
    if (!controleQuinquennal) return;
    const rows = [
      {
        id: controleQuinquennal.id,
        millesime: controleQuinquennal.millesime,
        entrepriseName: controleQuinquennal.entrepriseName,
        dateDebut: new Date(
          controleQuinquennal.dateDebut ?? controleQuinquennal.dateCreation
        ).toISOString(),
        dateDepot: controleQuinquennal.dateDepot
          ? new Date(controleQuinquennal.dateDepot).toISOString()
          : null,
        dateButoir:
          getControleQuinquennalDateButoir(
            controleQuinquennal
          )?.toISOString() ?? null,
      },
    ];
    void openDialog(CQCancelDialog, { rows }).then((confirmed) => {
      if (confirmed) annulationControle.mutate([controleQuinquennal.id]);
    });
  };

  const { categoriesActivites: declaredCategories } = entreprise;
  const oriasStatut = infoSocieteOrias?.statut;
  const isOriasInactive =
    oriasStatut === StatutSociete.JAMAIS_INSCRIT ||
    oriasStatut === StatutSociete.SUPPRIME;
  const oriasCategories = infoSocieteOrias?.categories ?? [];
  const isControleClos =
    !!controleQuinquennal &&
    isControleQuinquennalReadOnly(controleQuinquennal.pipelineStage);

  const hasCategoryDifference =
    !isOriasLoading &&
    !isOriasInactive &&
    (oriasCategories.length !== declaredCategories.length ||
      oriasCategories.some((cat) => !declaredCategories.includes(cat)));

  const formattedDate = useMemo(
    () =>
      latestRemediation?.creationDate
        ? formatDate(new Date(latestRemediation.creationDate))
        : undefined,
    [latestRemediation?.creationDate]
  );

  const latestCommentDate = formattedDate ? `du ${formattedDate}` : undefined;

  const axeName = axeData?.type ? axeTypeToLabel[axeData.type] : "";

  const categoryDifferenceWarning = hasCategoryDifference && (
    <div className={style.categoriesDifference}>
      <Chip
        color={ChipColor.Orange}
        label="Adhésions ORIAS détectées :"
        iconLeft={<Warning width={16} height={16} />}
      />
      <span>{formatList(oriasCategories)}</span>
    </div>
  );

  return (
    <div className={style.sidebarWrapper}>
      <button
        type="button"
        className={style.toggleButton}
        onClick={onToggle}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronRight /> : <ChevronLeft />}
      </button>
      <div
        className={classNames(style.sidebar, { [style.collapsed]: collapsed })}
      >
        {!collapsed && (
          <>
            <div className={style.entreprise}>
              {isRecette && isOriasInactive && (
                <InfoCard type="warning">
                  Entreprise non inscrite à l&apos;ORIAS.
                  {!isControleClos && (
                    <Button
                      className={style.cancelButton}
                      variant="outline-accent"
                      onClick={handleAnnulerControle}
                      disabled={
                        !controleQuinquennal || annulationControle.isPending
                      }
                    >
                      {annulationControle.isPending
                        ? "Annulation en cours..."
                        : "Annuler le contrôle"}
                    </Button>
                  )}
                </InfoCard>
              )}
              <div>
                <p>Société</p>
                <div className={style.entrepriseHeader}>
                  <h1>{entreprise.raisonSociale}</h1>
                  <EntrepriseActionsMenu
                    idEntreprise={entreprise.hubspotId}
                    siren={entreprise.siren}
                    idAxe={axeData.idAxe}
                    idControleQuinquennal={axeData.idControleQuinquennal}
                    idContactPrincipal={entreprise.idContactPrincipal}
                    idEntreprisePrincipal={entreprise.idEntreprisePrincipal}
                    categoriesActivites={entreprise.categoriesActivites}
                    categoriesActivitesAccessoires={
                      entreprise.categoriesActivitesAccessoires
                    }
                    avancement={avancementSociete}
                  />
                </div>
              </div>
              <ZoneInfo title="Axe" value={axeName} status={axeData.status} />
              <ZoneInfo title="Millésime" value={millesime.toString()} />
              <ZoneInfo
                title="Catégories d'activité"
                warning={categoryDifferenceWarning}
                value={formatList(entreprise.categoriesActivites)}
              />
              <ZoneInfo
                title="Catégories d'activité accessoires"
                value={formatList(entreprise.categoriesActivitesAccessoires)}
              />
            </div>
            {latestConversation && (
              <>
                <SidebarDelimiter />
                <div>
                  <h4>Dernier commentaire de remédiation</h4>
                  {latestCommentDate && (
                    <span className={style.commentDate}>
                      {latestCommentDate}
                    </span>
                  )}
                </div>
                <div className={style.commentContainer}>
                  <Conversation
                    millesime={millesime}
                    conversation={latestConversation}
                    siren={entreprise.siren}
                  />
                  <Button
                    className={style.commentButton}
                    variant="text"
                    onClick={() => setSidePaneState(true)}
                    iconVariant="right"
                    icon={<ArrowRight />}
                  >
                    Voir l&apos;échange
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
